/** @format */

import { apiRoomDetail } from '@/api/apiRoomDetail';
import ChevronRight from '@/assets/icons/joivy_icon_chevron_right_light.svg';
import CardRoom from '@/components/page/PageListing/components/CardRoom';
import { Icon } from '@/components/ui/Icon';
import { PopupPosition } from '@/ducks/map/hooks/useMap';
import { useAppTranslation } from '@/i18n/declarations';
import { NAME_PORTAL_CODE } from '@/utils/gatsby/constants';
import { StrictOmit } from '@/utils/gatsby/types';
import { useQueries } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { Popup } from 'react-map-gl';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import type { Coords, TBuildingRoomsMarker, TRoomMarker } from '../../../api/apiListingMapMarkers';
import { CardRoomSkeleton } from '../../page/PageListing/components/CardRoomSkeleton';
import BuildingIcon from './BuildingIcon';
import { PopupContent } from './MapPopup.style';
import { SlideIndex, StyledNavigation, StyledNavigationButton } from './SliderNavigation.style';
import { GTMMapInteraction } from '@/@ducks/googleTagManager/helpers/google-tag-manager';
import type { TRoomCard } from '@/@ducks/room/declarations';

const BuildingPopup = ({
  rooms,
  position,
  coords,
  setPopupInfo,
  offset,
}: {
  rooms: StrictOmit<TRoomMarker, 'position'>[];
  position: PopupPosition;
  coords: Coords;
  setPopupInfo: React.Dispatch<React.SetStateAction<((TRoomMarker | TBuildingRoomsMarker) & { id: string }) | null>>;
  offset: [number, number];
}) => {
  const { intl } = useAppTranslation();
  const locale = intl.locale;
  const roomLength = rooms.length;
  const swiperRef = useRef<SwiperRef>();
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(1);

  const queries = rooms.map(single => ({
    queryKey: ['room', single.code],
    queryFn: async () => apiRoomDetail({ roomCode: single.code, locale, portalCode: NAME_PORTAL_CODE }),
    staleTime: Infinity,
  }));

  const results = useQueries({
    queries: [...queries],
    combine: results => {
      return {
        data: results.map(result => result.data),
        isPending: results.some(result => result.isPending),
        isError: results.some(result => result.isError),
      };
    },
  });

  const handleNextSlide = () => {
    if (!swiperRef.current?.swiper) return;

    const currentIndex = swiperRef.current.swiper.activeIndex;
    let nextIndex: number;

    if (currentIndex === roomLength - 1) {
      nextIndex = 0;
    } else {
      nextIndex = currentIndex + 1;
    }

    swiperRef.current.swiper.slideTo(nextIndex);

    if (results.data[nextIndex]) {
      const { discountedPrice, basePrice, code } = results.data[nextIndex] as TRoomCard;
      GTMMapInteraction(discountedPrice || basePrice || 0, code ?? '');
    }
  };

  const handlePrevSlide = () => {
    if (!swiperRef.current?.swiper) return;

    const currentIndex = swiperRef.current.swiper.activeIndex;
    let prevIndex: number;

    if (currentIndex === 0) {
      prevIndex = roomLength - 1;
    } else {
      prevIndex = currentIndex - 1;
    }

    swiperRef.current.swiper.slideTo(prevIndex);

    if (results.data[prevIndex]) {
      const { discountedPrice, basePrice, code } = results.data[prevIndex] as TRoomCard;
      GTMMapInteraction(discountedPrice || basePrice || 0, code ?? '');
    }
  };

  return (
    <Popup
      maxWidth={'20.5rem'}
      anchor={position}
      longitude={Number(coords.longitude)}
      latitude={Number(coords.latitude)}
      onClose={() => setPopupInfo(null)}
      offset={offset}
    >
      <PopupContent>
        <StyledNavigation>
          <StyledNavigationButton onClick={handlePrevSlide}>
            <Icon
              icon={ChevronRight}
              color="black"
              width={28}
              height={28}
              role="presentation"
              style={{ transform: 'rotate(180deg)', cursor: 'pointer' }}
            />
          </StyledNavigationButton>

          <SlideIndex>
            <BuildingIcon style={{ width: 22 }} fill="#535c62" />
            {currentSlideIndex} / {roomLength}
          </SlideIndex>

          <StyledNavigationButton onClick={handleNextSlide}>
            <Icon
              icon={ChevronRight}
              color="black"
              width={28}
              height={28}
              role="presentation"
              style={{
                transform: 'rotate(0deg)',
                cursor: 'pointer',
              }}
            />
          </StyledNavigationButton>
        </StyledNavigation>
        <Swiper
          ref={swiperRef as React.RefObject<SwiperRef>}
          draggable
          slidesPerView={1}
          spaceBetween={10}
          onSlideChange={swiper => {
            setCurrentSlideIndex(swiper.activeIndex + 1);
          }}
        >
          {results.isPending || results.isError
            ? Array.from({ length: roomLength }).map((_, index) => (
                <SwiperSlide key={index}>
                  <CardRoomSkeleton />
                </SwiperSlide>
              ))
            : results.data.map(single =>
                single ? (
                  <SwiperSlide key={single.code}>
                    <CardRoom room={single} isPopup />
                  </SwiperSlide>
                ) : null,
              )}
        </Swiper>
      </PopupContent>
    </Popup>
  );
};

export default BuildingPopup;
